import React from "react";
import { ArrowRight, Award } from "lucide-react";
import { HERO_FOOD, HERO_STATS } from "../mock/data";

const Hero = () => {
  return (
    <section className="relative pt-[80px] bg-[#07070A] overflow-hidden">
      {/* Backdrop */}
      <div className="absolute inset-0 dot-grid opacity-50" />
      <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-[#14E0BF]/10 blur-[140px]" />
      <div className="absolute top-1/3 -left-40 w-[500px] h-[500px] rounded-full bg-[#F77F00]/8 blur-[140px]" />

      <div className="relative max-w-[1480px] mx-auto px-6 lg:px-10 pt-16 lg:pt-24 pb-0">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          {/* Left: copy */}
          <div className="lg:col-span-7 animate-fade-up">
            {/* UK's 1st Badge */}
            <div className="inline-flex items-center gap-3 mb-8">
              <div className="flex items-center gap-2 bg-[#F77F00]/10 border border-[#F77F00]/40 text-[#F77F00] px-3 py-1.5 rounded-full saffron-glow">
                <Award className="w-3.5 h-3.5" strokeWidth={2.5} />
                <span className="font-display text-[10px] uppercase tracking-[0.18em]">UK&rsquo;s 1st</span>
              </div>
              <span className="text-white/55 text-[13px] font-medium">Indian Virtual Kitchen Franchise</span>
            </div>

            <h1 className="text-white mb-8">
              <span className="block font-serif-display italic font-light text-[44px] md:text-[68px] lg:text-[84px] leading-[0.95] text-white/85">
                Cook Indian.
              </span>
              <span className="block font-display text-[60px] md:text-[92px] lg:text-[124px] leading-[0.88] mt-1">
                RUN <span className="text-[#14E0BF]">SMART.</span>
              </span>
              <span className="block font-display text-[60px] md:text-[92px] lg:text-[124px] leading-[0.88] -mt-2">
                EARN MORE.
              </span>
            </h1>

            <p className="text-white/70 text-lg lg:text-xl leading-relaxed mb-10 max-w-[560px]">
              Britain&rsquo;s pioneer Indian virtual brands franchise. Four delivery-only brands, one halal kitchen, zero rebuilds. Plug into our recipes, marketing and ops – and watch your weekly sales double.
            </p>

            <div className="flex flex-wrap items-center gap-4 mb-12">
              <a
                href="#journey"
                className="btn-pill inline-flex items-center gap-2 bg-[#14E0BF] hover:bg-[#0FB89C] text-[#07070A] font-display text-[13px] uppercase tracking-wider px-7 py-4 rounded-full"
              >
                Start Your Franchise
                <ArrowRight className="w-4 h-4" />
              </a>
              <a
                href="#brands"
                className="btn-pill inline-flex items-center gap-2 border border-white/20 hover:border-[#14E0BF] hover:text-[#14E0BF] text-white font-display text-[13px] uppercase tracking-wider px-7 py-4 rounded-full"
              >
                Meet The 4 Brands
              </a>
            </div>
          </div>

          {/* Right: editorial image with stat overlays */}
          <div className="lg:col-span-5 relative">
            <div className="relative aspect-[4/5] rounded-[28px] overflow-hidden border border-white/10 teal-glow-strong animate-scale-in">
              <img src={HERO_FOOD} alt="Signature biryani" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#07070A] via-[#07070A]/40 to-transparent" />

              {/* Floating tag */}
              <div className="absolute top-6 left-6 bg-[#07070A]/80 backdrop-blur-md border border-white/15 rounded-full px-4 py-2 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-[#14E0BF] animate-pulse-dot" />
                <span className="font-mono text-[11px] text-white/80 uppercase tracking-wider">
                  Live in 14 days
                </span>
              </div>

              {/* Bottom card */}
              <div className="absolute bottom-6 left-6 right-6 bg-[#14141B]/95 backdrop-blur-md border border-white/10 rounded-2xl p-5">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/50">
                    Featured Brand
                  </span>
                  <span
                    className="font-mono text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full"
                    style={{ color: "#14E0BF", background: "rgba(20,224,191,0.1)" }}
                  >
                    Bestseller
                  </span>
                </div>
                <div className="font-serif-display italic text-2xl text-white leading-tight">
                  Dum on the Run
                </div>
                <div className="text-white/55 text-sm">Hyderabadi Lamb Dum Biryani</div>
              </div>
            </div>

            {/* Floating stat card off the image */}
            <div className="hidden lg:block absolute -left-12 top-12 bg-[#14E0BF] rounded-2xl px-5 py-4 rotate-[-6deg] shadow-2xl">
              <div className="font-display text-[28px] text-[#07070A] leading-none">2x</div>
              <div className="font-mono text-[10px] text-[#07070A]/75 uppercase tracking-wider mt-1">
                weekly sales
              </div>
            </div>
          </div>
        </div>

        {/* Bottom stats strip */}
        <div className="mt-16 lg:mt-24 grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/8 rounded-[28px] overflow-hidden border border-white/10">
          {HERO_STATS.map((s, i) => (
            <div key={i} className="bg-[#07070A] p-6 lg:p-8 hover:bg-[#0E0E13] transition-colors">
              <div
                className={`font-display text-4xl lg:text-6xl mb-2 ${
                  i === 0 ? "text-[#F77F00]" : "text-white"
                }`}
              >
                {s.value}
              </div>
              <div className="text-white/55 text-xs lg:text-sm uppercase tracking-wider font-mono">
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Ticker strip */}
      <div className="mt-20 bg-[#14E0BF] py-4 overflow-hidden">
        <div className="flex animate-ticker whitespace-nowrap">
          {Array.from({ length: 12 }).map((_, i) => (
            <div key={i} className="flex items-center gap-6 mx-6">
              <span className="font-display text-[#07070A] text-lg uppercase tracking-[0.18em]">
                UK&rsquo;s 1st Indian Virtual Kitchen Franchise
              </span>
              <span className="w-2 h-2 rounded-full bg-[#07070A]" />
              <span className="font-mono text-[#07070A] text-sm uppercase tracking-wider">
                kitchnworx.co.uk
              </span>
              <span className="w-2 h-2 rounded-full bg-[#07070A]" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Hero;
